/**
 * conversations.store — Zustand UI-only slice for the conversations list rail.
 *
 * Per FRONTEND_PLAN.md §10:
 *   stores/conversations.store.ts — UI-only: active conv id, search, group-by
 *
 * Conversations themselves are SERVER state (TanStack Query under
 * `['sessions','list']`). This store keeps the bits of UI that don't belong
 * in the URL: the search box, the chosen group-by, and the local
 * `pinnedIds` set so a user can keep a conversation pinned across reloads
 * without us round-tripping the server.
 *
 * Pin state is the only thing we persist, keyed per-employee per-bindle so
 * two co-owners on the same Bindle don't share each other's pins.
 */
import { create } from 'zustand';
import { z } from 'zod';

const STORAGE_PREFIX = 'wayne.v1.conversations';

export type ConversationGroupBy = 'time' | 'agent';

const PersistedSchema = z.object({
  version: z.literal(1),
  pinnedIds: z.array(z.string()),
  groupBy: z.enum(['time', 'agent']).default('time'),
});

type Persisted = z.infer<typeof PersistedSchema>;

export interface ConversationsStoreState {
  activeConversationId: string | null;
  searchQuery: string;
  groupBy: ConversationGroupBy;
  pinnedIds: ReadonlySet<string>;
  hydratedFor: string | null;
  setActive: (conversationId: string | null) => void;
  setSearch: (q: string) => void;
  setGroupBy: (g: ConversationGroupBy) => void;
  togglePin: (conversationId: string) => void;
  hydrateFromStorage: (employeeId: string) => void;
  reset: () => void;
}

function storageKey(employeeId: string): string {
  return `${STORAGE_PREFIX}.${employeeId}`;
}

function readPersisted(employeeId: string): Persisted | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(storageKey(employeeId));
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    const result = PersistedSchema.safeParse(parsed);
    return result.success ? result.data : null;
  } catch {
    return null;
  }
}

function writePersisted(employeeId: string, value: Persisted): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(storageKey(employeeId), JSON.stringify(value));
  } catch {
    // ignore
  }
}

export const useConversationsStore = create<ConversationsStoreState>((set, get) => ({
  activeConversationId: null,
  searchQuery: '',
  groupBy: 'time',
  pinnedIds: new Set<string>(),
  hydratedFor: null,
  setActive: (conversationId) => set({ activeConversationId: conversationId }),
  setSearch: (q) => set({ searchQuery: q }),
  setGroupBy: (g) => {
    const employeeId = get().hydratedFor;
    set({ groupBy: g });
    if (employeeId) {
      writePersisted(employeeId, {
        version: 1,
        pinnedIds: Array.from(get().pinnedIds),
        groupBy: g,
      });
    }
  },
  togglePin: (conversationId) => {
    const next = new Set(get().pinnedIds);
    if (next.has(conversationId)) {
      next.delete(conversationId);
    } else {
      next.add(conversationId);
    }
    set({ pinnedIds: next });
    const employeeId = get().hydratedFor;
    if (employeeId) {
      writePersisted(employeeId, {
        version: 1,
        pinnedIds: Array.from(next),
        groupBy: get().groupBy,
      });
    }
  },
  hydrateFromStorage: (employeeId) => {
    const stored = readPersisted(employeeId);
    set({
      hydratedFor: employeeId,
      pinnedIds: new Set<string>(stored?.pinnedIds ?? []),
      groupBy: stored?.groupBy ?? 'time',
    });
  },
  reset: () =>
    set({
      activeConversationId: null,
      searchQuery: '',
      groupBy: 'time',
      pinnedIds: new Set<string>(),
      hydratedFor: null,
    }),
}));
